import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, FileText, PenLine, Eraser, FileDown, Landmark, ShieldCheck } from "lucide-react";

/** Public engagement signing page — NO authentication. Each required document is signed separately. */
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const DOCS = {
  loe: { title: "Letter of Engagement", field: "letter_html",
    accept: "I have read and accept the terms set out in this Letter of Engagement, including the scope of services, fees, limitation of liability, data protection and termination clauses." },
  msa: { title: "Master Services Agreement & Statement of Work", field: "msa_html",
    accept: "I have read and accept the Master Services Agreement and the Statement of Work, including the services, fees and terms set out in them." },
};

export default function OnboardingSign() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  function load() {
    return axios.get(`${BACKEND}/api/public/onboarding/${token}`)
      .then((r) => setDoc(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Could not load engagement letter."))
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]"><Loader2 size={28} className="animate-spin text-[var(--navy)]" /></div>;
  }
  if (err && !doc) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
        <div className="card-flat p-8 max-w-md text-center">
          <FileText size={40} className="mx-auto text-[var(--ink-2)] mb-3" />
          <h1 className="text-xl font-serif-strong mb-2">Link unavailable</h1>
          <p className="text-sm text-[var(--ink-2)]">{err}</p>
        </div>
      </div>
    );
  }
  if (doc.status === "signed") {
    const docs = doc.required_docs || ["loe"];
    const ddDone = ["active", "pending"].includes(doc.dd_status);
    return ddDone ? <DoneScreen name={doc.signer_name} token={token} docs={docs} /> : <DDStep doc={doc} token={token} docs={docs} onDone={load} />;
  }

  const required = doc.required_docs || ["loe"];
  return (
    <div className="min-h-screen bg-[var(--bg)] py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <img src="/brand/logo.png" alt="Finchley Business Services" className="h-10 w-auto" />
          <div>
            <div className="font-serif-strong text-lg">Finchley Business Services</div>
            <div className="text-xs text-[var(--ink-2)]">
              {required.length > 1 ? `Review & sign — ${required.length} documents, each signed separately` : `${DOCS[required[0]].title} — review & sign`}
            </div>
          </div>
        </div>
        <Summary doc={doc} />
        {required.map((k, i) => (
          <DocSignCard key={k} n={required.length > 1 ? i + 1 : null} docKey={k} token={token} html={doc[DOCS[k].field]}
            signed={doc.signed_docs?.[k]} defaultName={doc.signer_name || ""} onSigned={load} />
        ))}
        <div className="mt-4 text-center text-xs text-[var(--ink-2)]">A signed copy will be sent to {doc.email} and your Finchley Business Services contact.</div>
      </div>
    </div>
  );
}

function DDStep({ doc, token, docs, onDone }) {
  const returning = new URLSearchParams(window.location.search).get("dd") === "return";
  const [busy, setBusy] = useState(returning);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!returning) return;
    axios.get(`${BACKEND}/api/public/onboarding/${token}/dd/status`)
      .then(() => onDone())
      .catch(() => setErr("We couldn't confirm your Direct Debit just yet — please try again in a moment."))
      .finally(() => setBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function start() {
    setErr(""); setBusy(true);
    try {
      const r = await axios.post(`${BACKEND}/api/public/onboarding/${token}/dd/start`);
      window.location.assign(r.data.authorisation_url);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not open the Direct Debit page. Please try again.");
      setBusy(false);
    }
  }

  const failed = ["failed", "cancelled"].includes(doc.dd_status);
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg)] p-4">
      <div className="card-flat p-8 max-w-lg w-full" data-testid="dd-step">
        <div className="flex items-center gap-2 text-[var(--ok)] text-sm mb-4">
          <CheckCircle2 size={16} /> Thank you{doc.signer_name ? `, ${doc.signer_name}` : ""} — your engagement documents are signed.
        </div>
        <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--ink-2)] mb-1">Step 2 of 2</div>
        <h1 className="text-xl font-serif-strong mb-2 flex items-center gap-2"><Landmark size={20} /> Set up your Direct Debit</h1>
        <p className="text-sm text-[var(--ink-2)] mb-2">
          Our fees are collected by Direct Debit. Please authorise a Direct Debit mandate for Finchley Business Services.
        </p>
        <p className="text-sm mb-4" data-testid="dd-no-payment-note"><b>No payment is taken today.</b> Your invoices will be sent to you separately and collected under this mandate.</p>
        {failed && <div className="mb-3 p-2 rounded text-sm border border-[var(--err)] text-[var(--err)] bg-[var(--err-bg)]" data-testid="dd-failed-note">Your previous Direct Debit set-up didn't complete. Please try again, or use a different account.</div>}
        {err && <div className="mb-3 p-2 rounded text-sm border border-[var(--err)] text-[var(--err)] bg-[var(--err-bg)]" data-testid="dd-error">{err}</div>}
        <button data-testid="dd-start" onClick={start} disabled={busy} className="btn-primary w-full flex items-center justify-center gap-2 text-sm !py-2.5">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Landmark size={14} />} {busy ? "Please wait…" : "Set up Direct Debit"}
        </button>
        <div className="flex items-center gap-1.5 text-[11px] text-[var(--ink-2)] mt-3"><ShieldCheck size={12} /> Secure page provided by GoCardless. We never see or store your bank details.</div>
        <div className="border-t border-[var(--line)] mt-5 pt-4 flex flex-col items-start gap-2">
          {docs.map((k) => <PdfLink key={k} token={token} docKey={k} />)}
        </div>
      </div>
    </div>
  );
}

function PdfLink({ token, docKey, label }) {
  return (
    <a href={`${BACKEND}/api/public/onboarding/${token}/signed/${docKey}/pdf`} data-testid={`public-pdf-${docKey}`}
      className="btn-ghost inline-flex items-center gap-1.5 text-xs !py-1.5 !px-3">
      <FileDown size={13} /> {label || `Download signed ${DOCS[docKey].title}`}
    </a>
  );
}

function DoneScreen({ name, token, docs }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg)] p-4">
      <div className="card-flat p-8 max-w-md text-center" data-testid="sign-done">
        <CheckCircle2 size={48} className="mx-auto text-[var(--ok)] mb-3" />
        <h1 className="text-xl font-serif-strong mb-2">You're all set</h1>
        <p className="text-sm text-[var(--ink-2)] mb-4">Thank you{name ? `, ${name}` : ""}. Your engagement is signed and your Direct Debit has been set up. The team at Finchley Business Services will be in touch shortly with next steps.</p>
        <div className="flex flex-col items-center gap-2 mb-4">
          {docs.map((k) => <PdfLink key={k} token={token} docKey={k} />)}
        </div>
        <p className="text-xs text-[var(--ink-2)]">You can safely close this window.</p>
      </div>
    </div>
  );
}

function Summary({ doc }) {
  const row = (label, v) => <div><span className="text-[var(--ink-2)]">{label}:</span> <strong>{v}</strong></div>;
  return (
    <div className="card-flat p-5 mb-4 bg-[var(--bg-2)]">
      <div className="text-xs text-[var(--ink-2)] mb-2">Summary of this engagement</div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        {row("Client", doc.client_name)}
        {doc.plan?.label && row("Plan", doc.plan.label)}
        {Number(doc.accounting_annual_fee) > 0 && row("Annual fee", `£${Number(doc.accounting_annual_fee).toFixed(2)}`)}
        {doc.company_name && row("Company", doc.company_name)}
        {row("Monthly fee", `£${Number(doc.monthly_fee || 0).toFixed(2)}`)}
        {Number(doc.one_off_fee) > 0 && <div className="col-span-2">{row("One-off", `£${Number(doc.one_off_fee).toFixed(2)} — ${doc.one_off_label || "set-up"}`)}</div>}
      </div>
    </div>
  );
}

function DocSignCard({ n, docKey, token, html, signed, defaultName, onSigned }) {
  const meta = DOCS[docKey];
  const [name, setName] = useState(defaultName);
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const pad = useRef(null);

  async function submit() {
    setErr("");
    if (!name.trim()) { setErr("Please enter your full name."); return; }
    if (!accepted) { setErr("Please tick the box to accept the terms."); return; }
    if (!pad.current.hasDrawn()) { setErr("Please draw your signature in the box."); return; }
    setBusy(true);
    try {
      await axios.post(`${BACKEND}/api/public/onboarding/${token}/sign`, {
        signer_name: name.trim(), signature_png: pad.current.toPng(), accepted_terms: true, document: docKey,
      });
      await onSigned();
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not submit signature. Please try again.");
    } finally { setBusy(false); }
  }

  return (
    <div className="mb-6" data-testid={`doc-card-${docKey}`}>
      <div className="flex items-center gap-2 mb-2">
        {n && <span className="w-6 h-6 rounded-full bg-[var(--navy)] text-white text-xs flex items-center justify-center">{n}</span>}
        <h2 className="font-serif-strong text-base">{meta.title}</h2>
        {signed && <span className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-[var(--ok)] text-[var(--ok)] bg-[var(--ok-bg)]" data-testid={`doc-signed-${docKey}`}><CheckCircle2 size={10} /> Signed</span>}
      </div>
      <div className="card-flat p-6 mb-3 max-h-[520px] overflow-y-auto">
        <div className="prose max-w-none text-sm leading-relaxed" data-testid={docKey === "loe" ? "letter-content" : "msa-content"} dangerouslySetInnerHTML={{ __html: html }} />
      </div>
      {signed ? (
        <div className="card-flat p-4 text-sm bg-[var(--ok-bg)] border-[var(--ok)]">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span>Signed by <strong>{signed.signer_name}</strong> on {new Date(signed.signed_at).toLocaleDateString("en-GB")}.</span>
            <PdfLink token={token} docKey={docKey} label="Download signed copy (PDF)" />
          </div>
        </div>
      ) : (
        <div className="card-flat p-5">
          <div className="overline mb-3">Your signature — {meta.title}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-xs text-[var(--ink-2)] block mb-1">Full name</label>
              <input data-testid={`sign-name-${docKey}`} value={name} onChange={(e) => setName(e.target.value)} placeholder="Type your full legal name" className="input text-sm w-full" />
            </div>
            <div>
              <label className="text-xs text-[var(--ink-2)] block mb-1">Date</label>
              <input readOnly value={new Date().toLocaleDateString("en-GB")} className="input text-sm w-full bg-[var(--bg-2)]" />
            </div>
          </div>
          <SignaturePad ref={pad} testid={`signature-canvas-${docKey}`} />
          <label className="flex items-start gap-2 mt-4 cursor-pointer">
            <input type="checkbox" data-testid={`accept-terms-${docKey}`} checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-1" />
            <span className="text-sm">{meta.accept}</span>
          </label>
          {err && <div className="mt-3 p-2 bg-[var(--err-bg)] border border-[var(--err)] text-[var(--err)] rounded text-sm">{err}</div>}
          <div className="mt-4 flex justify-end">
            <button onClick={submit} disabled={busy} data-testid={`submit-signature-${docKey}`} className="btn-primary flex items-center gap-2 text-sm !py-2.5 !px-5">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              {busy ? "Submitting…" : `Sign ${docKey === "loe" ? "Letter of Engagement" : "MSA & SOW"}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export const SignaturePad = React.forwardRef(function SignaturePad({ testid }, ref) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const drawn = useRef(false);

  function paintBlank() {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    drawn.current = false;
  }
  useEffect(() => {
    const c = canvasRef.current;
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    Object.assign(ctx, { lineCap: "round", lineJoin: "round", lineWidth: 2, strokeStyle: "#0E1A3A" });
    paintBlank();
  }, []);
  React.useImperativeHandle(ref, () => ({ hasDrawn: () => drawn.current, toPng: () => canvasRef.current.toDataURL("image/png") }));

  function pos(e) {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches && e.touches[0];
    return { x: (t ? t.clientX : e.clientX) - rect.left, y: (t ? t.clientY : e.clientY) - rect.top };
  }
  function start(e) { e.preventDefault(); drawing.current = true; const { x, y } = pos(e); const ctx = canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(x, y); }
  function move(e) { if (!drawing.current) return; e.preventDefault(); const { x, y } = pos(e); const ctx = canvasRef.current.getContext("2d"); ctx.lineTo(x, y); ctx.stroke(); drawn.current = true; }
  function end() { drawing.current = false; }

  return (
    <>
      <label className="text-xs text-[var(--ink-2)] mb-1 flex items-center gap-1"><PenLine size={12} /> Draw your signature below</label>
      <div className="border-2 border-dashed border-[var(--line)] rounded-md bg-white">
        <canvas ref={canvasRef} data-testid={testid}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          style={{ width: "100%", height: 160, cursor: "crosshair", display: "block" }} />
      </div>
      <div className="flex justify-end mt-1">
        <button onClick={paintBlank} data-testid={`clear-${testid}`} className="text-xs text-[var(--ink-2)] hover:text-[var(--ink)] flex items-center gap-1"><Eraser size={12} /> Clear</button>
      </div>
    </>
  );
});
